import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "../styles/DateTimePicker.scss";
import "../styles/LongTermRangePicker.scss";
import { useLanguage } from "../context/LanguageContext";
import {
  buildBlockedDateSet,
  normalizeDateValue,
  parseDateValue,
  toDateKey,
} from "../utils/dateUtils";
import { formatShortDate } from "../utils/dateFormatting";
import {
  DATE_TIME_PICKER_MAX_MINUTES,
  DATE_TIME_PICKER_STEP,
} from "../utils/dateTimePickerConstants";

const pad2 = (value) => String(value).padStart(2, "0");

const setTimeOnDate = (date, value) => {
  const parsed = parseDateValue(date);
  if (!(parsed instanceof Date) || Number.isNaN(parsed.getTime())) return null;
  const [hourRaw, minuteRaw] = String(value || "").split(":");
  const hours = Number.parseInt(hourRaw, 10);
  const minutes = Number.parseInt(minuteRaw, 10);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  parsed.setHours(hours, minutes, 0, 0);
  return parsed;
};

const formatTimeValue = (date) =>
  date instanceof Date && !Number.isNaN(date.getTime())
    ? `${pad2(date.getHours())}:${pad2(date.getMinutes())}`
    : "09:00";
const formatOptionalTimeValue = (date) =>
  date instanceof Date && !Number.isNaN(date.getTime())
    ? `${pad2(date.getHours())}:${pad2(date.getMinutes())}`
    : "—";

const isSameDay = (left, right) =>
  left instanceof Date &&
  right instanceof Date &&
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate();

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const buildDateWithMinutes = (baseDate, totalMinutes) => {
  const parsed = parseDateValue(baseDate);
  if (!(parsed instanceof Date) || Number.isNaN(parsed.getTime())) return null;
  parsed.setHours(Math.floor(totalMinutes / 60), totalMinutes % 60, 0, 0);
  return parsed;
};

const getMinutesOfDay = (date) =>
  date instanceof Date && !Number.isNaN(date.getTime())
    ? date.getHours() * 60 + date.getMinutes()
    : 0;

const RangeTimelineTimeCard = ({
  label,
  date,
  accentClass,
  onChange,
  minimumDateTime = null,
  minimumMinutes = 0,
  maximumMinutes = DATE_TIME_PICKER_MAX_MINUTES,
  locale,
  disabled = false,
}) => {
  const STEP = DATE_TIME_PICKER_STEP;
  const containerRef = useRef(null);
  const minutesRef = useRef(getMinutesOfDay(date));
  const windTimeoutRef = useRef(null);
  const [minutes, setMinutes] = useState(() => getMinutesOfDay(date));
  const [isDragging, setIsDragging] = useState(false);
  const [dragDirection, setDragDirection] = useState(null);

  const minMinutes = useMemo(() => {
    const minimumFromWindow = Number.isFinite(minimumMinutes) ? minimumMinutes : 0;
    const bound = parseDateValue(minimumDateTime);
    const minimumFromLeadTime =
      bound && date && isSameDay(bound, date)
        ? Math.min(
            DATE_TIME_PICKER_MAX_MINUTES,
            Math.ceil(getMinutesOfDay(bound) / STEP) * STEP
          )
        : 0;
    return Math.max(minimumFromWindow, minimumFromLeadTime);
  }, [date, minimumDateTime, minimumMinutes, STEP]);

  const maxMinutes = useMemo(() => {
    const safeMaximum = Number.isFinite(maximumMinutes)
      ? maximumMinutes
      : DATE_TIME_PICKER_MAX_MINUTES;
    return Math.max(minMinutes, Math.min(safeMaximum, DATE_TIME_PICKER_MAX_MINUTES));
  }, [maximumMinutes, minMinutes]);

  const displayTime = `${pad2(Math.floor(minutes / 60))}:${pad2(minutes % 60)}`;
  const percent = minutes / 1440;
  const timelineLimit = Math.max(maxMinutes, DATE_TIME_PICKER_STEP);
  const hasWorkingWindow = maxMinutes > minMinutes;
  const workingStartPercent = Math.max(0, Math.min(100, (minMinutes / timelineLimit) * 100));
  const workingEndPercent = 100;
  const workingWindowWidth = Math.max(workingEndPercent - workingStartPercent, 0);
  const dragDirectionClass =
    isDragging && dragDirection ? `dragging-${dragDirection}` : "";

  const emitMinutes = useCallback(
    (nextMinutes) => {
      const nextDate = buildDateWithMinutes(date, nextMinutes);
      if (nextDate) onChange(nextDate);
    },
    [date, onChange]
  );

  useEffect(() => {
    const currentMinutes = getMinutesOfDay(date);
    const nextMinutes = clamp(currentMinutes, minMinutes, maxMinutes);
    setMinutes(nextMinutes);
    minutesRef.current = nextMinutes;

    if (date && currentMinutes !== nextMinutes) {
      emitMinutes(nextMinutes);
    }
  }, [date, emitMinutes, maxMinutes, minMinutes]);

  const clearWindTimeout = useCallback(() => {
    if (windTimeoutRef.current) {
      clearTimeout(windTimeoutRef.current);
      windTimeoutRef.current = null;
    }
  }, []);

  const updateDragDirection = useCallback((nextMinutes) => {
    const previousMinutes = minutesRef.current;
    if (nextMinutes === previousMinutes) return;
    const nextDirection = nextMinutes > previousMinutes ? "right" : "left";
    setDragDirection(nextDirection);
    clearWindTimeout();
    windTimeoutRef.current = setTimeout(() => {
      setDragDirection(null);
      windTimeoutRef.current = null;
    }, 140);
  }, [clearWindTimeout]);

  const percentFromClientX = useCallback((clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect || rect.width <= 0) return 0;
    return clamp((clientX - rect.left) / rect.width, 0, 1);
  }, []);

  const minutesFromPercent = useCallback((value) => {
    const raw = Math.round((value * 1440) / STEP) * STEP;
    return clamp(raw, minMinutes, maxMinutes);
  }, [STEP, maxMinutes, minMinutes]);

  const applyClientX = useCallback((clientX) => {
    const nextMinutes = minutesFromPercent(percentFromClientX(clientX));
    if (nextMinutes === minutesRef.current) return;
    updateDragDirection(nextMinutes);
    minutesRef.current = nextMinutes;
    setMinutes(nextMinutes);
    emitMinutes(nextMinutes);
  }, [emitMinutes, minutesFromPercent, percentFromClientX, updateDragDirection]);

  const getClientX = (event) => {
    if (event.touches?.length) return event.touches[0].clientX;
    if (event.changedTouches?.length) return event.changedTouches[0].clientX;
    return event.clientX;
  };

  const preventDefaultIfNeeded = (event) => {
    if ((event.touches || event.changedTouches) && event.cancelable) {
      event.preventDefault();
    }
  };

  const handlePointerDown = (event) => {
    if (disabled) return;
    const clientX = getClientX(event);
    if (clientX == null) return;
    preventDefaultIfNeeded(event);
    setIsDragging(true);
    applyClientX(clientX);
  };

  const stepBy = useCallback(
    (delta) => {
      if (disabled) return;
      const next = clamp(minutesRef.current + delta, minMinutes, maxMinutes);
      if (next === minutesRef.current) return;
      minutesRef.current = next;
      setMinutes(next);
      emitMinutes(next);
    },
    [disabled, emitMinutes, maxMinutes, minMinutes]
  );

  useEffect(() => {
    if (!isDragging || disabled) return undefined;

    const handleMove = (event) => {
      const clientX = getClientX(event);
      if (clientX == null) return;
      preventDefaultIfNeeded(event);
      applyClientX(clientX);
    };

    const handleUp = () => {
      setIsDragging(false);
      clearWindTimeout();
      setDragDirection(null);
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    window.addEventListener("touchmove", handleMove, { passive: false });
    window.addEventListener("touchend", handleUp);
    window.addEventListener("touchcancel", handleUp);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleUp);
      window.removeEventListener("touchcancel", handleUp);
    };
  }, [applyClientX, clearWindTimeout, disabled, isDragging]);

  return (
    <div
      className={[
        "timepicker-card",
        "longterm-picker__time-card",
        disabled ? "longterm-picker__time-card--disabled" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="longterm-picker__time-card-label">{label}</div>
      <div className="longterm-picker__time-card-date">
        {date instanceof Date && !Number.isNaN(date.getTime())
          ? formatShortDate(date, locale)
          : "—"}
      </div>
      <div
        ref={containerRef}
        className="timeline-container longterm-picker__timeline"
        onMouseDown={handlePointerDown}
        onTouchStart={handlePointerDown}
        role="slider"
        aria-valuemin={minMinutes}
        aria-valuemax={maxMinutes}
        aria-valuenow={minutes}
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onKeyDown={(event) => {
          if (disabled) return;
          if (event.key === "ArrowLeft") stepBy(-STEP);
          if (event.key === "ArrowRight") stepBy(STEP);
          if (event.key === "Home") {
            if (minMinutes !== minutesRef.current) {
              minutesRef.current = minMinutes;
              setMinutes(minMinutes);
              emitMinutes(minMinutes);
            }
          }
          if (event.key === "End") {
            if (maxMinutes !== minutesRef.current) {
              minutesRef.current = maxMinutes;
              setMinutes(maxMinutes);
              emitMinutes(maxMinutes);
            }
          }
        }}
      >
        {hasWorkingWindow && (
          <div className="working-window" aria-hidden="true">
            <span
              className="working-window__line"
              style={{
                left: `${workingStartPercent}%`,
                width: `${workingWindowWidth}%`,
              }}
            />
            <span
              className="working-window__line working-window__line--ghost"
              style={{
                left: `${workingStartPercent}%`,
                width: `${workingWindowWidth}%`,
              }}
            />
            <span
              className="working-window__edge working-window__edge--start"
              style={{ left: `${workingStartPercent}%` }}
            />
            <span
              className="working-window__edge working-window__edge--end"
              style={{ left: `${workingEndPercent}%` }}
            />
          </div>
        )}
        <div className="timeline-hour-ticks">
          {Array.from({ length: 25 }).map((_, index) => {
            const tickMinutes = Math.min(index * 60, DATE_TIME_PICKER_MAX_MINUTES);
            const isActive = tickMinutes >= minMinutes && tickMinutes <= maxMinutes;
            return (
              <span
                key={index}
                className={`tick ${index % 6 === 0 ? "tick-lg" : ""} ${
                  isActive ? "tick--active" : "tick--inactive"
                }`}
              />
            );
          })}
        </div>
        <div
          className={`time-marker ${accentClass} ${isDragging ? "dragging" : ""} ${dragDirectionClass}`.trim()}
          style={{ left: `${percent * 100}%` }}
        >
          <div className="marker-wind" aria-hidden="true">
            <span />
            <span />
            <span />
            <span />
          </div>
          <div className="marker-label">{displayTime}</div>
        </div>
      </div>
    </div>
  );
};

const LongTermRangePicker = ({
  startDate,
  endDate,
  onRangeChange,
  blockedDates,
  disabledWeekdays,
  selectableStartDate,
  selectableEndDate,
  minimumStartDateTime,
  startMinMinutes = 0,
  startMaxMinutes = DATE_TIME_PICKER_MAX_MINUTES,
  endMinMinutes = 0,
  endMaxMinutes = DATE_TIME_PICKER_MAX_MINUTES,
}) => {
  const { t, language } = useLanguage();
  const locale = language === "de" ? "de-AT" : "en-GB";
  const blockedDateSet = useMemo(() => buildBlockedDateSet(blockedDates), [blockedDates]);
  const disabledWeekdaysSet = useMemo(
    () => new Set((Array.isArray(disabledWeekdays) ? disabledWeekdays : []).map(Number)),
    [disabledWeekdays]
  );
  const minDay = useMemo(() => normalizeDateValue(selectableStartDate, false), [selectableStartDate]);
  const maxDay = useMemo(() => normalizeDateValue(selectableEndDate, true), [selectableEndDate]);
  const minimumStart = useMemo(() => parseDateValue(minimumStartDateTime), [minimumStartDateTime]);
  const startSelection = useMemo(() => parseDateValue(startDate), [startDate]);
  const endSelection = useMemo(() => parseDateValue(endDate), [endDate]);
  const [currentView, setCurrentView] = useState(() => {
    const anchor = startSelection ?? minimumStart ?? new Date();
    return new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  });
  const [draftStart, setDraftStart] = useState(() => startSelection);
  const [activeSelection, setActiveSelection] = useState(() =>
    startSelection && !endSelection ? "end" : "start"
  );

  useEffect(() => {
    setDraftStart(startSelection ?? null);
    setActiveSelection(startSelection && !endSelection ? "end" : "start");
    const anchor = startSelection ?? minimumStart ?? new Date();
    setCurrentView((prev) => {
      if (prev.getFullYear() === anchor.getFullYear() && prev.getMonth() === anchor.getMonth()) {
        return prev;
      }
      return new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    });
  }, [endSelection, minimumStart, startSelection]);

  const monthFormatter = useMemo(
    () => new Intl.DateTimeFormat(locale, { month: "long" }),
    [locale]
  );

  const formattedHeaderDate = (value) =>
    value instanceof Date && !Number.isNaN(value.getTime())
      ? formatShortDate(value, locale)
      : "—";

  const dayNames = useMemo(() => {
    const monday = new Date(Date.UTC(2023, 0, 2));
    const formatter = new Intl.DateTimeFormat(locale, { weekday: "short" });
    return Array.from({ length: 7 }, (_, index) =>
      formatter.format(new Date(monday.getTime() + index * 86400000))
    );
  }, [locale]);

  const isSelectableDay = useCallback((date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return false;
    const normalized = normalizeDateValue(date, false);
    if (!normalized) return false;
    if (minDay && normalized < minDay) return false;
    if (maxDay && normalized > maxDay) return false;
    if (disabledWeekdaysSet.has((normalized.getDay() + 6) % 7)) return false;
    const key = toDateKey(normalized);
    if (key && blockedDateSet.has(key)) return false;
    return true;
  }, [blockedDateSet, disabledWeekdaysSet, maxDay, minDay]);

  const days = useMemo(() => {
    const year = currentView.getFullYear();
    const month = currentView.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date();
    const firstDay = new Date(year, month, 1);
    const startOffset = (firstDay.getDay() + 6) % 7;
    const totalCells = Math.ceil((daysInMonth + startOffset) / 7) * 7;

    return Array.from({ length: totalCells }, (_, index) => {
      const dayNumber = index - startOffset + 1;
      const inCurrentMonth = dayNumber > 0 && dayNumber <= daysInMonth;
      const date = new Date(year, month, dayNumber);
      const selectable = inCurrentMonth && isSelectableDay(date);
      const blocked = inCurrentMonth && !selectable;
      const isToday =
        inCurrentMonth &&
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear();
      const inRange =
        draftStart &&
        endSelection &&
        normalizeDateValue(date, false) >= normalizeDateValue(draftStart, false) &&
        normalizeDateValue(date, false) <= normalizeDateValue(endSelection, false);

      return {
        date,
        dayNumber: inCurrentMonth ? dayNumber : "",
        inCurrentMonth,
        selectable,
        blocked,
        isToday,
        isStart: draftStart ? isSameDay(date, draftStart) : false,
        isEnd: endSelection ? isSameDay(date, endSelection) : false,
        inRange,
      };
    });
  }, [currentView, draftStart, endSelection, isSelectableDay]);

  const emitRange = (nextStart, nextEnd) => {
    if (typeof onRangeChange !== "function") return;
    onRangeChange({
      startDate: nextStart,
      endDate: nextEnd,
    });
  };

  const handleDayClick = (day) => {
    if (!day.selectable) return;
    const clicked = new Date(day.date);
    const activeStart = draftStart ?? startSelection ?? null;
    const startTime = formatTimeValue(activeStart ?? minimumStart ?? clicked);
    const endTime = formatTimeValue(activeStart ?? clicked);

    if (activeSelection === "start" || !activeStart) {
      const nextStart = setTimeOnDate(clicked, startTime);
      setDraftStart(nextStart);
      setActiveSelection("end");
      emitRange(nextStart, null);
      return;
    }

    if (normalizeDateValue(clicked, false) < normalizeDateValue(activeStart, false)) {
      const nextStart = setTimeOnDate(clicked, startTime);
      setDraftStart(nextStart);
      setActiveSelection("end");
      emitRange(nextStart, null);
      return;
    }

    const nextEnd = setTimeOnDate(clicked, endTime);
    setActiveSelection("start");
    emitRange(activeStart, nextEnd);
  };

  const handleTimeChange = (field, value) => {
    const nextStart = field === "start" ? setTimeOnDate(startSelection, value) : startSelection;
    const nextEnd = field === "end" ? setTimeOnDate(endSelection, value) : endSelection;
    emitRange(nextStart, nextEnd);
  };

  return (
    <div className="datepicker longterm-picker">
      <div className="datepicker-header">
        <div className="datepicker-title">
          {t("bookingDay.range.title", "Choose your rental range")}
        </div>
        <div className="datepicker-subheader longterm-picker__subheader">
          <button
            type="button"
            className={[
              "longterm-picker__subheader-block",
              activeSelection === "start" ? "is-active" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={() => setActiveSelection("start")}
          >
            <span className="longterm-picker__subheader-topline">
              <span className="longterm-picker__subheader-dot" aria-hidden="true" />
              <span className="longterm-picker__subheader-label">
                {t("bookingDay.range.startShort", "Start")}
              </span>
            </span>
            <span className="longterm-picker__subheader-value">
              <span className="subheader-date">{formattedHeaderDate(startSelection)}</span>
              <span className="subheader-time">{formatOptionalTimeValue(startSelection)}</span>
            </span>
          </button>
          <div className="longterm-picker__subheader-bridge" aria-hidden="true">
            <span className="longterm-picker__subheader-rail" />
          </div>
          <button
            type="button"
            className={[
              "longterm-picker__subheader-block",
              activeSelection === "end" ? "is-active" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={() => {
              if (draftStart) setActiveSelection("end");
            }}
            disabled={!draftStart}
          >
            <span className="longterm-picker__subheader-topline">
              <span className="longterm-picker__subheader-dot" aria-hidden="true" />
              <span className="longterm-picker__subheader-label">
                {t("bookingDay.range.endShort", "End")}
              </span>
            </span>
            <span className="longterm-picker__subheader-value">
              <span className="subheader-date">{formattedHeaderDate(endSelection)}</span>
              <span className="subheader-time">
                {endSelection ? formatTimeValue(endSelection) : "—"}
              </span>
            </span>
          </button>
        </div>
      </div>

      <div className="datepicker-body">
        <div className="datepicker-calendar">
          <div className="calendar-header">
            <div
              className="goback"
              onClick={() =>
                setCurrentView(new Date(currentView.getFullYear(), currentView.getMonth() - 1, 1))
              }
            >
              <svg width="30" height="30">
                <path fill="none" stroke="#0DAD83" strokeWidth="3" d="M19,6 l-9,9 l9,9" />
              </svg>
            </div>
            <div className="current-month-container">
              {monthFormatter.format(currentView)} {currentView.getFullYear()}
            </div>
            <div
              className="goforward"
              onClick={() =>
                setCurrentView(new Date(currentView.getFullYear(), currentView.getMonth() + 1, 1))
              }
            >
              <svg width="30" height="30">
                <path fill="none" stroke="#0DAD83" strokeWidth="3" d="M11,6 l9,9 l-9,9" />
              </svg>
            </div>
          </div>

          <div className="calendar-day-header">
            {dayNames.map((day) => (
              <span key={day} className="day-label">
                {day}
              </span>
            ))}
          </div>

          <div className="calendar-grid">
            {days.map((day, index) => (
              <div
                key={`${toDateKey(day.date) || "blank"}-${index}`}
                className={[
                  "datecontainer",
                  day.inRange ? "longterm-picker__cell--range" : "",
                  day.inRange && day.isStart ? "longterm-picker__cell--range-start" : "",
                  day.inRange && day.isEnd ? "longterm-picker__cell--range-end" : "",
                  day.inRange && index % 7 !== 0 && days[index - 1]?.inRange
                    ? "longterm-picker__cell--range-continue-left"
                    : "",
                  day.inRange && index % 7 !== 6 && days[index + 1]?.inRange
                    ? "longterm-picker__cell--range-continue-right"
                    : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <div
                  className={[
                    "datenumber",
                    !day.inCurrentMonth ? "old" : "",
                    day.isToday ? "today" : "",
                    day.blocked ? "blocked" : "",
                    !day.selectable ? "disabled" : "",
                    day.inRange ? "longterm-picker__day--range" : "",
                    day.isStart ? "day-selected longterm-picker__day--start" : "",
                    day.isEnd ? "longterm-picker__day--end" : "",
                    endSelection && (day.isStart || day.isEnd)
                      ? "longterm-picker__day--active-anchor"
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => handleDayClick(day)}
                  role="button"
                  aria-disabled={!day.selectable}
                >
                  {day.dayNumber}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="datepicker-side longterm-picker__side">
          <RangeTimelineTimeCard
            label={t("bookingDay.range.start", "Start date & time")}
            date={startSelection}
            accentClass="longterm-picker__marker--start"
            locale={locale}
            minimumDateTime={minimumStart}
            minimumMinutes={startMinMinutes}
            maximumMinutes={startMaxMinutes}
            disabled={!startSelection}
            onChange={(nextDate) => handleTimeChange("start", formatTimeValue(nextDate))}
          />

          <RangeTimelineTimeCard
            label={t("bookingDay.range.end", "End date & time")}
            date={endSelection}
            accentClass="longterm-picker__marker--end"
            locale={locale}
            minimumMinutes={endMinMinutes}
            maximumMinutes={endMaxMinutes}
            disabled={!endSelection}
            onChange={(nextDate) => handleTimeChange("end", formatTimeValue(nextDate))}
          />
        </div>
      </div>
    </div>
  );
};

export default LongTermRangePicker;
